import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import useGeneratorStore, { FormFieldKeys } from '@/state/generator-store';
import { isEmpty } from 'lodash-es';
import React, { useEffect } from 'react';
import { useController } from 'react-hook-form';
import { cn } from '@/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CardSelectorProps {
  name: FormFieldKeys;
  showName: string;
  options: {
    displayName?: string;
    version: string;
    name: string;
    label: string;
  }[];
  showLabel?: boolean;
  showVersionTag?: boolean;
  showDropdown?: boolean;
  defaultValue?: string;
}

const CardSelector: React.FC<CardSelectorProps> = ({
  name,
  showName,
  options = [],
  showLabel = false,
  showDropdown = false,
  showVersionTag = false,
  defaultValue,
}) => {
  const { register, control, generatorForm } = useGeneratorStore((state) => ({
    register: state.register,
    control: state.control,
    generatorForm: state.generatorForm,
  }));

  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
  });

  const { setValue } = generatorForm ?? {};

  useEffect(() => {
    register(name);
    if (!isEmpty(defaultValue)) {
      onChange(defaultValue);
      setValue?.(name, defaultValue);
    }
  }, [register, name]);

  const selectedItem = options.find((item) => item.version === value);

  return (
    <div className="bg-[#333435] rounded-lg w-[4.5rem] flex flex-col items-center justify-center p-1 ml-1 mr-1">
      <div className="text-gray-400 text-[0.8rem] font-pressura-mono py-1">{showName}</div>
      <div className="flex flex-col justify-between bg-[#202223] w-full h-16">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={options.length <= 1}>
            <button
              className={cn('text-white bg-jen-orange hover:bg-jen-orange-60 w-full transition duration-200 text-[0.6rem] pt-[0.15rem]', {
                'opacity-30': options.length <= 1,
              })}
              style={{
                visibility: showDropdown ? 'visible' : 'hidden',
              }}
              type="button"
            >
              ▼
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-26 bg-white">
            {options.map((item) => (
              <DropdownMenuRadioItem
                key={item.version}
                value={item.version}
                className="hover:text-jen-orange cursor-pointer text-[0.8rem]"
                onClick={() => {
                  onChange(item.version);
                  setValue?.(name, item.version);
                }}
              >
                {item.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className={`flex flex-col items-center justify-center h-full ${showDropdown && showVersionTag ? 'mt-[-0.5rem]' : ''}`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                  className="text-white font-normal text-[1rem] w-14 flex items-center justify-center"
                >
                  {(selectedItem?.displayName ?? value) !== undefined
                    ? ((selectedItem?.displayName ?? value) as string).length > 4
                      ? `${((selectedItem?.displayName ?? value) as string).slice(0, 4)}...`
                      : `${selectedItem?.displayName ?? value}`
                    : ''}
                </span>
              </TooltipTrigger>
              <TooltipContent className="text-[#9D9DA6] bg-zinc-800 border-zinc-700">{value}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div
            className="text-gray-400 text-[0.65rem]"
            style={{
              visibility: showLabel ? 'visible' : 'hidden',
            }}
          >
            {selectedItem?.name}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardSelector;
